import { useState, useCallback, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { Label, Input, Card } from "@tellescope/design-system";
import { Button, Alert, AlertDescription, LanguageSelector } from "@tellescope/ui";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { i18n } from "@tellescope/i18n";
import { AuthPageLayout } from "../components/AuthPageLayout";
import { useEnduserSetPassword, useEnduserDismissPasswordPrompt } from "../store/hooks";

interface PasswordRequirement {
  key: string;
  label: string;
  validate: (password: string) => boolean;
}

const getPasswordRequirements = (): PasswordRequirement[] => [
  {
    key: "length",
    label: i18n.t("Password must be at least 8 characters", { ns: "auth" }),
    validate: (p) => p.length >= 8,
  },
  {
    key: "uppercase",
    label: i18n.t("Password must contain an uppercase letter", { ns: "auth" }),
    validate: (p) => /[A-Z]/.test(p),
  },
  {
    key: "lowercase",
    label: i18n.t("Password must contain a lowercase letter", { ns: "auth" }),
    validate: (p) => /[a-z]/.test(p),
  },
  {
    key: "numberOrSpecial",
    label: i18n.t("Password must contain a number or special character", { ns: "auth" }),
    validate: (p) => /[0-9@#!$%^&*()]/.test(p),
  },
];

export const SetPasswordPage = () => {
  const { t } = useTranslation("auth");
  const setPassword = useEnduserSetPassword();
  const dismissPasswordPrompt = useEnduserDismissPasswordPrompt();

  const [password, setPasswordValue] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordValidation = getPasswordRequirements().map((req) => ({
    ...req,
    isValid: req.validate(password),
  }));

  const isPasswordValid = passwordValidation.every((req) => req.isValid);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = useCallback(async () => {
    if (!isPasswordValid || !passwordsMatch) return;

    setIsLoading(true);
    setError(null);

    const success = await setPassword(password);

    if (!success) {
      setError(t("Failed to set password. Please try again."));
    }
    setIsLoading(false);
  }, [isPasswordValid, passwordsMatch, setPassword, password, t]);

  return (
    <AuthPageLayout title={t("Set Your Password")} description={t("Create a password to sign in directly next time")}>
      <Card.Body className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password">{t("Password")}</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPasswordValue(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {password.length > 0 && (
          <ul className="space-y-1 text-sm">
            {passwordValidation.map((req) => (
              <li
                key={req.key}
                className={`flex items-center gap-2 ${req.isValid ? "text-green-600" : "text-muted-foreground"}`}
              >
                {req.isValid ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                {req.label}
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-2">
          <Label htmlFor="confirm-password">{t("Confirm Password")}</Label>
          <div className="relative">
            <Input
              id="confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              tabIndex={-1}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {confirmPassword.length > 0 && !passwordsMatch && (
            <p className="text-sm text-destructive">{t("Passwords do not match")}</p>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </Card.Body>

      <Card.Footer className="flex flex-col gap-3">
        <Button onClick={handleSubmit} disabled={!isPasswordValid || !passwordsMatch || isLoading} className="w-full">
          {isLoading ? t("Setting...") : t("Set Password")}
        </Button>

        <Button variant="ghost" onClick={dismissPasswordPrompt} className="w-full text-muted-foreground">
          {t("Skip for now")}
        </Button>

        <div className="w-full flex justify-center pt-2">
          <LanguageSelector />
        </div>
      </Card.Footer>
    </AuthPageLayout>
  );
};
